import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Switch } from '../components/ui/switch';
import {
  Bell,
  Car,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Circle,
  Clock,
  Compass,
  CreditCard,
  EyeOff,
  GraduationCap,
  Heart,
  HelpCircle,
  Home,
  Luggage,
  PartyPopper,
  Plane,
  PlugZap,
  RotateCcw,
  Shield,
  ShieldCheck,
  Sparkles,
  Trash2,
  Users,
  UtensilsCrossed,
  Wifi,
  X,
} from 'lucide-react';
import { journeyAPI } from '../lib/api';
import {
  SNOOZE_OPTIONS_DAYS,
  TASK_STATUS,
  auditActionKey,
  categoryCounts,
  categoryLabelKey,
  helpPromptKey,
  partitionCategories,
  snoozeLabelKey,
  recommendationKey,
  taskDescKey,
  taskNameKey,
  taskStatus,
} from '../lib/journeyUtils';

// Visual metadata only — category ids and ordering come from the backend template.
const CATEGORY_VISUALS = {
  housing: { icon: Home, color: 'text-blue-500' },
  utilities: { icon: PlugZap, color: 'text-amber-500' },
  internet: { icon: Wifi, color: 'text-sky-500' },
  banking: { icon: CreditCard, color: 'text-emerald-600' },
  insurance: { icon: Shield, color: 'text-cyan-600' },
  schools: { icon: GraduationCap, color: 'text-purple-500' },
  vehicle: { icon: Car, color: 'text-slate-600' },
  healthcare: { icon: Heart, color: 'text-red-500' },
  'local-life': { icon: Compass, color: 'text-teal-600' },
  restaurants: { icon: UtensilsCrossed, color: 'text-orange-500' },
  events: { icon: PartyPopper, color: 'text-pink-500' },
  'weekend-trips': { icon: Luggage, color: 'text-indigo-500' },
  family: { icon: Users, color: 'text-rose-500' },
  'military-pcs': { icon: Plane, color: 'text-military-gold' },
};

const categoryVisual = (categoryId) =>
  CATEGORY_VISUALS[categoryId] || { icon: Circle, color: 'text-slate-500' };

export default function LifeJourneyPage() {
  const { user, loading: authLoading } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [journey, setJourney] = useState(null);
  const [progress, setProgress] = useState(null);
  const [template, setTemplate] = useState(null);
  const [preferences, setPreferences] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [reminders, setReminders] = useState(null); // null = not checked yet
  const [helpRequests, setHelpRequests] = useState([]);
  const [auditEvents, setAuditEvents] = useState(null); // null = collapsed
  const [error, setError] = useState('');

  const [helpDialog, setHelpDialog] = useState(null); // {categoryId, taskId}
  const [snoozeMenuTask, setSnoozeMenuTask] = useState(null);

  const loadJourneyExtras = useCallback(async () => {
    const [prefsRes, recsRes, helpRes] = await Promise.all([
      journeyAPI.getPreferences(),
      journeyAPI.getRecommendations(),
      journeyAPI.getHelpRequests(),
    ]);
    setPreferences(prefsRes.data.preferences);
    setRecommendations(recsRes.data.recommendations || []);
    setHelpRequests(helpRes.data.help_requests || []);
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const templatesRes = await journeyAPI.getTemplates();
      const catalog = templatesRes.data.templates || [];
      let journeyData = null;
      try {
        const journeyRes = await journeyAPI.getMine();
        journeyData = journeyRes.data;
      } catch (err) {
        if (err?.response?.status !== 404) throw err;
      }
      if (journeyData) {
        setJourney(journeyData.journey);
        setProgress(journeyData.progress);
        setTemplate(
          catalog.find((tpl) => tpl.id === journeyData.journey.template_id) || catalog[0] || null
        );
        await loadJourneyExtras();
      } else {
        setJourney(null);
        setTemplate(catalog[0] || null);
      }
    } catch (err) {
      console.error('Failed to load journey:', err);
      setError(t('journey.error.load', 'Could not load your journey. Please try again.'));
    } finally {
      setLoading(false);
    }
  }, [t, loadJourneyExtras]);

  useEffect(() => {
    if (authLoading) return;
    if (user) {
      loadAll();
    } else {
      setLoading(false);
    }
  }, [user, authLoading, loadAll]);

  const applyJourneyResponse = (data) => {
    setJourney(data.journey);
    setProgress(data.progress);
  };

  const handleJourneyStarted = async (data) => {
    applyJourneyResponse(data);
    // Preferences are created server-side at opt-in; load them (plus
    // recommendations and help requests) so the reminder settings and
    // privacy panels are usable immediately, not only after a reload.
    try {
      await loadJourneyExtras();
    } catch (err) {
      console.error('Failed to load journey panels after start:', err);
    }
  };

  const refreshRecommendations = async () => {
    try {
      const res = await journeyAPI.getRecommendations();
      setRecommendations(res.data.recommendations || []);
    } catch (err) {
      console.error('Failed to refresh recommendations:', err);
    }
  };

  const handleTaskAction = async (taskId, action, snoozeDays) => {
    setSnoozeMenuTask(null);
    try {
      const res = await journeyAPI.applyTaskAction(taskId, {
        action,
        snooze_days: snoozeDays ?? null,
      });
      setJourney((prev) =>
        prev
          ? { ...prev, task_states: { ...(prev.task_states || {}), [taskId]: res.data.state } }
          : prev
      );
      setProgress(res.data.progress);
      refreshRecommendations();
    } catch (err) {
      console.error('Task action failed:', err);
      setError(t('journey.error.action', 'That action could not be saved. Please try again.'));
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-slate-100 pt-20 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-signal-blue border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 pt-20 pb-16" data-testid="life-journey-page">
      <PageHero t={t} journey={journey} progress={progress} />
      <div className="max-w-6xl mx-auto px-4">
        {error && (
          <div className="mt-6 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">
            {error}
          </div>
        )}

        {!user && <SignInCard t={t} navigate={navigate} />}

        {user && !journey && template && (
          <StartJourneyCard t={t} template={template} onStarted={handleJourneyStarted} />
        )}

        {user && journey && template && (
          <>
            {recommendations.length > 0 && (
              <RecommendationsStrip t={t} recommendations={recommendations} />
            )}
            <JourneyControls
              t={t}
              journey={journey}
              onStatusChange={async (status) => {
                const res = await journeyAPI.updateStatus(status);
                applyJourneyResponse(res.data);
              }}
            />
            <RemindersCard
              t={t}
              journey={journey}
              preferences={preferences}
              reminders={reminders}
              onCheck={async () => {
                const res = await journeyAPI.getDueReminders(true);
                setReminders(res.data);
              }}
            />
            <Checklist
              t={t}
              template={template}
              journey={journey}
              snoozeMenuTask={snoozeMenuTask}
              setSnoozeMenuTask={setSnoozeMenuTask}
              onAction={handleTaskAction}
              onNeedHelp={(categoryId, taskId) => setHelpDialog({ categoryId, taskId })}
            />
            <PreferencesCard
              t={t}
              preferences={preferences}
              onUpdate={async (patch) => {
                const res = await journeyAPI.updatePreferences(patch);
                setPreferences(res.data.preferences);
              }}
            />
            <HelpRequestsCard
              t={t}
              helpRequests={helpRequests}
              onCancel={async (id) => {
                await journeyAPI.cancelHelpRequest(id);
                const res = await journeyAPI.getHelpRequests();
                setHelpRequests(res.data.help_requests || []);
              }}
            />
            <PrivacyCard
              t={t}
              auditEvents={auditEvents}
              onToggleAudit={async () => {
                if (auditEvents) {
                  setAuditEvents(null);
                  return;
                }
                const res = await journeyAPI.getAudit();
                setAuditEvents(res.data.events || []);
              }}
              onDeleteAll={async () => {
                await journeyAPI.deleteMyData();
                setJourney(null);
                setProgress(null);
                setPreferences(null);
                setReminders(null);
                setRecommendations([]);
                setHelpRequests([]);
                setAuditEvents(null);
              }}
            />
          </>
        )}
      </div>

      {helpDialog && (
        <NeedHelpDialog
          t={t}
          categoryId={helpDialog.categoryId}
          taskId={helpDialog.taskId}
          navigate={navigate}
          onClose={() => setHelpDialog(null)}
          onCreated={async () => {
            const res = await journeyAPI.getHelpRequests();
            setHelpRequests(res.data.help_requests || []);
            refreshRecommendations();
          }}
        />
      )}
    </div>
  );
}

function PageHero({ t, journey, progress }) {
  return (
    <div className="bg-gradient-to-b from-champagne-100 to-ivory border-b border-champagne-200 py-8">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-signal-blue/10 border border-signal-blue/30 rounded-full mb-3">
              <Compass className="w-4 h-4 text-signal-blue" />
              <span className="text-signal-blue text-sm">
                {t('journey.badge', 'Life Journey')}
              </span>
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-2 tracking-tight">
              {t('journey.title', 'YOUR RELOCATION JOURNEY')}
            </h1>
            <p className="text-slate-600 max-w-2xl">
              {t(
                'journey.subtitle',
                'Pick your goals, track every step from housing to weekend trips, and ask JC for help whenever you get stuck.'
              )}
            </p>
          </div>
          {journey && progress && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 min-w-[280px]">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-slate-600">
                  {t('journey.progress.required', 'Required steps')}
                </span>
                <span className="text-lg font-bold text-military-gold">
                  {progress.required_percent}%
                </span>
              </div>
              <Progress value={progress.required_percent} className="h-2 mb-3" />
              <div className="flex items-center justify-between text-xs text-slate-800">
                <span>
                  {progress.required_completed}/{progress.required_total}{' '}
                  {t('journey.progress.required_suffix', 'required')}
                </span>
                <span>
                  {progress.completed}/{progress.total}{' '}
                  {t('journey.progress.total_suffix', 'total')}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SignInCard({ t, navigate }) {
  return (
    <div className="mt-8 bg-white rounded-xl shadow-sm border border-slate-200 p-8 text-center max-w-xl mx-auto">
      <Compass className="w-10 h-10 text-signal-blue mx-auto mb-4" />
      <h2 className="font-heading text-xl font-bold text-slate-900 mb-2">
        {t('journey.signin.title', 'Sign in to start your journey')}
      </h2>
      <p className="text-slate-600 text-sm mb-6">
        {t(
          'journey.signin.body',
          'Your journey is private and opt-in. Sign in to choose your goals and save your progress.'
        )}
      </p>
      <Button
        onClick={() => navigate('/login?redirect=/journey')}
        className="bg-signal-blue hover:bg-signal-blue-hover"
      >
        {t('journey.signin.cta', 'Sign In')}
      </Button>
    </div>
  );
}

function StartJourneyCard({ t, template, onStarted }) {
  const [selected, setSelected] = useState(() =>
    template.categories.filter((cat) => cat.kind === 'core').map((cat) => cat.id)
  );
  const [targetDate, setTargetDate] = useState('');
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [startError, setStartError] = useState('');

  const toggle = (categoryId) => {
    setSelected((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const start = async () => {
    setSubmitting(true);
    setStartError('');
    try {
      const res = await journeyAPI.start({
        template_id: template.id,
        selected_categories: selected,
        target_date: targetDate || null,
        consent_journey_data: consent,
      });
      onStarted(res.data);
    } catch (err) {
      console.error('Failed to start journey:', err);
      setStartError(
        t('journey.error.start', 'Could not start your journey. Please try again.')
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-8 bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
      <div className="p-6 border-b border-slate-200 bg-slate-50">
        <h2 className="font-heading text-xl font-bold text-slate-900 mb-1">
          {t('journey.start.title', 'Choose your goals')}
        </h2>
        <p className="text-sm text-slate-600">
          {t(
            'journey.start.subtitle',
            'Select what matters for your move. You can change your goals anytime.'
          )}
        </p>
      </div>
      <div className="p-6">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-6">
          {template.categories.map((category) => {
            const visual = categoryVisual(category.id);
            const Icon = visual.icon;
            const isOn = selected.includes(category.id);
            return (
              <button
                key={category.id}
                onClick={() => toggle(category.id)}
                data-testid={`goal-${category.id}`}
                className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-left text-sm transition-colors ${
                  isOn
                    ? 'bg-signal-blue/10 border-signal-blue/40 text-slate-900'
                    : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                }`}
              >
                <Icon className={`w-4 h-4 flex-shrink-0 ${visual.color}`} />
                <span className="flex-1 truncate">
                  {t(categoryLabelKey(category.id), category.id)}
                </span>
                {isOn && <CheckCircle2 className="w-4 h-4 text-signal-blue flex-shrink-0" />}
              </button>
            );
          })}
        </div>

        <div className="flex flex-col md:flex-row gap-6 md:items-end mb-6">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              <CalendarDays className="w-4 h-4 inline mr-2" />
              {t('journey.start.target_date', 'Expected arrival date (optional)')}
            </label>
            <input
              type="date"
              value={targetDate}
              onChange={(e) => setTargetDate(e.target.value)}
              className="bg-white border border-slate-300 rounded-md px-3 py-2 text-slate-800"
              data-testid="journey-target-date"
            />
            <p className="text-xs text-slate-500 mt-1">
              {t(
                'journey.start.target_date_hint',
                'Used only to time your reminders. Never tracked from your device.'
              )}
            </p>
          </div>
        </div>

        <label className="flex items-start gap-3 mb-6 cursor-pointer">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            className="mt-1"
            data-testid="journey-consent-checkbox"
          />
          <span className="text-sm text-slate-700">
            {t(
              'journey.start.consent',
              'I agree that my selected goals, task progress, and optional arrival date are stored to power my journey. Reminders stay off until I turn them on. I can delete all journey data anytime.'
            )}
          </span>
        </label>

        {startError && <p className="text-sm text-red-600 mb-4">{startError}</p>}

        <Button
          onClick={start}
          disabled={!consent || selected.length === 0 || submitting}
          className="bg-signal-blue hover:bg-signal-blue-hover"
          data-testid="journey-start-button"
        >
          {submitting
            ? t('journey.start.starting', 'Starting…')
            : t('journey.start.cta', 'Start My Journey')}
        </Button>
      </div>
    </div>
  );
}

function RecommendationsStrip({ t, recommendations }) {
  return (
    <div className="mt-6 bg-white rounded-xl shadow-sm border border-military-gold/40 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-4 h-4 text-military-gold" />
        <span className="text-sm font-medium text-slate-800">
          {t('journey.rec.title', 'JC suggests')}
        </span>
      </div>
      <ul className="space-y-2">
        {recommendations.map((rec, idx) => (
          <li key={`${rec.type}-${idx}`} className="text-sm text-slate-700 flex items-start gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-military-gold mt-1.5 flex-shrink-0" />
            {t(recommendationKey(rec), 'Take the next step on your journey.')}
            {rec.task_id && (
              <span className="text-slate-500">
                — {t(taskNameKey(rec.task_id), rec.task_id)}
              </span>
            )}
            {!rec.task_id && rec.category_id && (
              <span className="text-slate-500">
                — {t(categoryLabelKey(rec.category_id), rec.category_id)}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function JourneyControls({ t, journey, onStatusChange }) {
  const paused = journey.status === 'paused';
  return (
    <div className="mt-6 flex items-center justify-between bg-white rounded-xl shadow-sm border border-slate-200 px-4 py-3">
      <div className="flex items-center gap-2 text-sm text-slate-700">
        {paused ? (
          <>
            <EyeOff className="w-4 h-4 text-slate-500" />
            {t('journey.status.paused', 'Journey paused — all reminders are off.')}
          </>
        ) : (
          <>
            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
            {t('journey.status.active', 'Journey active')}
          </>
        )}
      </div>
      <Button
        size="sm"
        variant="outline"
        onClick={() => onStatusChange(paused ? 'active' : 'paused')}
        data-testid="journey-pause-toggle"
      >
        {paused
          ? t('journey.status.resume_cta', 'Resume journey')
          : t('journey.status.pause_cta', 'Pause journey')}
      </Button>
    </div>
  );
}

function RemindersCard({ t, journey, preferences, reminders, onCheck }) {
  const remindersOn = Boolean(
    preferences?.consent?.reminders && preferences?.reminders_enabled
  );
  const [checking, setChecking] = useState(false);

  return (
    <div className="mt-6 bg-white rounded-xl shadow-sm border border-slate-200 p-4">
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="flex items-center gap-2">
          <Bell className="w-4 h-4 text-signal-blue" />
          <span className="text-sm font-medium text-slate-800">
            {t('journey.reminders.title', 'Reminders')}
          </span>
        </div>
        <Button
          size="sm"
          variant="outline"
          disabled={!remindersOn || journey.status === 'paused' || checking}
          onClick={async () => {
            setChecking(true);
            try {
              await onCheck();
            } finally {
              setChecking(false);
            }
          }}
          data-testid="journey-check-reminders"
        >
          {checking
            ? t('journey.reminders.checking', 'Checking…')
            : t('journey.reminders.check_cta', 'Check reminders now')}
        </Button>
      </div>

      {!remindersOn && (
        <p className="text-sm text-slate-600">
          {t(
            'journey.reminders.off_hint',
            'Reminders are off. Turn them on in Reminder settings below — nothing is sent without your consent.'
          )}
        </p>
      )}

      {remindersOn && reminders === null && (
        <p className="text-sm text-slate-600">
          {t(
            'journey.reminders.not_checked',
            'Due reminders appear here and in your notifications when you check.'
          )}
        </p>
      )}

      {remindersOn && reminders && reminders.reminders.length === 0 && (
        <p className="text-sm text-slate-600">
          {t('journey.reminders.none', 'Nothing due right now. Enjoy the calm!')}
        </p>
      )}

      {remindersOn && reminders && reminders.reminders.length > 0 && (
        <ul className="mt-2 space-y-2">
          {reminders.reminders.map((reminder) => (
            <li
              key={reminder.dedupe_key}
              className="flex items-center gap-2 text-sm text-slate-700"
            >
              <Clock
                className={`w-4 h-4 flex-shrink-0 ${
                  reminder.overdue ? 'text-red-500' : 'text-military-gold'
                }`}
              />
              <span>{t(taskNameKey(reminder.task_id), reminder.task_id)}</span>
              {reminder.overdue && (
                <Badge className="text-[10px] px-1.5 py-0 bg-red-500/15 text-red-500 border-red-500/30">
                  {t('journey.reminders.overdue', 'Overdue')}
                </Badge>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Checklist({
  t,
  template,
  journey,
  snoozeMenuTask,
  setSnoozeMenuTask,
  onAction,
  onNeedHelp,
}) {
  const { selected } = useMemo(
    () => partitionCategories(template, journey.selected_categories),
    [template, journey.selected_categories]
  );
  const taskStates = journey.task_states || {};

  return (
    <div className="mt-6 space-y-6">
      {selected.map((category) => {
        const visual = categoryVisual(category.id);
        const Icon = visual.icon;
        const counts = categoryCounts(category, taskStates);
        return (
          <div
            key={category.id}
            className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
          >
            <div className="flex items-center gap-3 p-4 bg-slate-50 border-b border-slate-200">
              <Icon className={`w-5 h-5 ${visual.color}`} />
              <h2 className="font-heading text-lg font-bold text-slate-800">
                {t(categoryLabelKey(category.id), category.id)}
              </h2>
              <Badge variant="outline" className="ml-auto text-xs">
                {counts.completed}/{counts.total}
              </Badge>
            </div>
            <div className="divide-y divide-slate-200">
              {category.tasks.map((task) => (
                <TaskRow
                  key={task.id}
                  t={t}
                  categoryId={category.id}
                  task={task}
                  state={taskStates[task.id]}
                  snoozeOpen={snoozeMenuTask === task.id}
                  onToggleSnoozeMenu={() =>
                    setSnoozeMenuTask(snoozeMenuTask === task.id ? null : task.id)
                  }
                  onAction={onAction}
                  onNeedHelp={onNeedHelp}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TaskRow({
  t,
  categoryId,
  task,
  state,
  snoozeOpen,
  onToggleSnoozeMenu,
  onAction,
  onNeedHelp,
}) {
  const status = taskStatus({ [task.id]: state }, task.id);
  const completed = status === TASK_STATUS.COMPLETED;
  const snoozed = status === TASK_STATUS.SNOOZED;
  const dismissed = status === TASK_STATUS.DISMISSED;

  return (
    <div
      className={`p-4 transition-colors ${
        completed ? 'bg-emerald-500/5' : dismissed ? 'bg-slate-50 opacity-70' : 'hover:bg-slate-50'
      }`}
      data-testid={`journey-task-${task.id}`}
    >
      <div className="flex items-start gap-3">
        <button
          onClick={() => onAction(task.id, completed ? 'reopen' : 'complete')}
          className={`mt-0.5 flex-shrink-0 p-2 -m-2 transition-colors ${
            completed ? 'text-emerald-500' : 'text-slate-500 hover:text-slate-700'
          }`}
          aria-label={t(taskNameKey(task.id), task.id)}
          data-testid={`journey-check-${task.id}`}
        >
          {completed ? <CheckCircle2 className="w-5 h-5" /> : <Circle className="w-5 h-5" />}
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span
              className={`font-medium text-slate-800 ${completed ? 'line-through' : ''} ${
                dismissed ? 'line-through text-slate-500' : ''
              }`}
            >
              {t(taskNameKey(task.id), task.id)}
            </span>
            {task.required && (
              <Badge className="text-[10px] px-1.5 py-0 bg-red-500/20 text-red-400 border-red-500/30">
                {t('journey.task.required_badge', 'Required')}
              </Badge>
            )}
            {snoozed && (
              <Badge className="text-[10px] px-1.5 py-0 bg-military-gold/15 text-military-gold border-military-gold/30">
                <Clock className="w-3 h-3 mr-1" />
                {t('journey.task.snoozed_badge', 'Snoozed')}
              </Badge>
            )}
            {dismissed && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                {t('journey.task.dismissed_badge', 'Hidden')}
              </Badge>
            )}
          </div>
          <p className="text-sm text-slate-600">{t(taskDescKey(task.id), '')}</p>

          {!completed && (
            <div className="mt-2 flex items-center gap-3 flex-wrap">
              {!dismissed && (
                <div className="relative">
                  <button
                    onClick={onToggleSnoozeMenu}
                    className="text-xs text-slate-600 hover:text-slate-800 flex items-center gap-1"
                    data-testid={`journey-snooze-${task.id}`}
                  >
                    <Clock className="w-3 h-3" />
                    {t('journey.task.snooze_cta', 'Snooze')}
                    {snoozeOpen ? (
                      <ChevronUp className="w-3 h-3" />
                    ) : (
                      <ChevronDown className="w-3 h-3" />
                    )}
                  </button>
                  {snoozeOpen && (
                    <div className="absolute z-10 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg py-1 min-w-[140px]">
                      {SNOOZE_OPTIONS_DAYS.map((days) => (
                        <button
                          key={days}
                          onClick={() => onAction(task.id, 'snooze', days)}
                          className="block w-full text-left px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50"
                        >
                          {t(snoozeLabelKey(days), `${days} days`)}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <button
                onClick={() => onNeedHelp(categoryId, task.id)}
                className="text-xs text-signal-blue hover:text-signal-blue-hover flex items-center gap-1"
                data-testid={`journey-help-${task.id}`}
              >
                <HelpCircle className="w-3 h-3" />
                {t('journey.task.help_cta', 'Need help')}
              </button>
              {dismissed ? (
                <button
                  onClick={() => onAction(task.id, 'reopen')}
                  className="text-xs text-slate-600 hover:text-slate-800 flex items-center gap-1"
                >
                  <RotateCcw className="w-3 h-3" />
                  {t('journey.task.reopen_cta', 'Restore')}
                </button>
              ) : (
                <button
                  onClick={() => onAction(task.id, 'dismiss')}
                  className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1"
                >
                  <EyeOff className="w-3 h-3" />
                  {t('journey.task.dismiss_cta', 'Not relevant')}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PreferencesCard({ t, preferences, onUpdate }) {
  if (!preferences) return null;
  const consentOn = Boolean(preferences.consent?.reminders);
  const enabled = Boolean(preferences.reminders_enabled);

  return (
    <div className="mt-6 bg-white rounded-xl shadow-sm border border-slate-200 p-4">
      <div className="flex items-center gap-2 mb-4">
        <Bell className="w-4 h-4 text-signal-blue" />
        <span className="text-sm font-medium text-slate-800">
          {t('journey.prefs.title', 'Reminder settings')}
        </span>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-sm text-slate-800">
              {t('journey.prefs.consent_label', 'Allow journey reminders')}
            </p>
            <p className="text-xs text-slate-500">
              {t(
                'journey.prefs.consent_hint',
                'Explicit opt-in. Without this, the engine never computes or sends reminders.'
              )}
            </p>
          </div>
          <Switch
            checked={consentOn}
            onCheckedChange={(checked) =>
              onUpdate({ consent_reminders: checked, reminders_enabled: checked })
            }
            data-testid="journey-consent-reminders"
          />
        </div>

        {consentOn && (
          <div className="flex items-center justify-between gap-4">
            <p className="text-sm text-slate-800">
              {t('journey.prefs.enabled_label', 'Reminders active')}
            </p>
            <Switch
              checked={enabled}
              onCheckedChange={(checked) => onUpdate({ reminders_enabled: checked })}
              data-testid="journey-reminders-enabled"
            />
          </div>
        )}

        <div>
          <p className="text-sm text-slate-800 mb-2">
            {t('journey.prefs.channels_label', 'Channels')}
          </p>
          <div className="flex flex-wrap gap-2">
            <Badge className="bg-signal-blue/10 text-signal-blue border-signal-blue/30">
              {t('journey.prefs.channel_in_app', 'In-app')}
            </Badge>
            {['email', 'sms', 'push'].map((channel) => (
              <Badge key={channel} variant="outline" className="text-slate-400">
                {t(`journey.prefs.channel_${channel}`, channel)}{' '}
                · {t('journey.prefs.channel_coming_soon', 'not yet available')}
              </Badge>
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-1">
            {t(
              'journey.prefs.channels_hint',
              'Only in-app notifications exist today. Email, SMS, and push are not built yet and nothing is ever auto-sent.'
            )}
          </p>
        </div>
      </div>
    </div>
  );
}

function HelpRequestsCard({ t, helpRequests, onCancel }) {
  const open = helpRequests.filter((req) => req.status === 'open');
  if (open.length === 0) return null;
  return (
    <div className="mt-6 bg-white rounded-xl shadow-sm border border-slate-200 p-4">
      <div className="flex items-center gap-2 mb-3">
        <HelpCircle className="w-4 h-4 text-signal-blue" />
        <span className="text-sm font-medium text-slate-800">
          {t('journey.help.open_title', 'Your open help requests')}
        </span>
      </div>
      <ul className="space-y-2">
        {open.map((req) => (
          <li key={req.id} className="flex items-center gap-2 text-sm text-slate-700">
            <span className="flex-1">
              {t(categoryLabelKey(req.category_id), req.category_id)}
              {req.task_id && (
                <span className="text-slate-500"> — {t(taskNameKey(req.task_id), req.task_id)}</span>
              )}
            </span>
            <button
              onClick={() => onCancel(req.id)}
              className="text-xs text-slate-500 hover:text-slate-700"
            >
              {t('journey.help.cancel_cta', 'Cancel')}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function PrivacyCard({ t, auditEvents, onToggleAudit, onDeleteAll }) {
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  return (
    <div className="mt-6 bg-white rounded-xl shadow-sm border border-slate-200 p-4">
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck className="w-4 h-4 text-emerald-600" />
        <span className="text-sm font-medium text-slate-800">
          {t('journey.privacy.title', 'Privacy & data')}
        </span>
      </div>
      <p className="text-sm text-slate-600 mb-4">
        {t(
          'journey.privacy.body',
          'Your journey stores only your selected goals, task progress, optional arrival date, reminder settings, and help requests. No location tracking. You can see every action below and erase everything at once.'
        )}
      </p>

      <div className="flex flex-wrap gap-3">
        <Button size="sm" variant="outline" onClick={onToggleAudit} data-testid="journey-audit-toggle">
          {auditEvents
            ? t('journey.privacy.hide_activity', 'Hide activity log')
            : t('journey.privacy.show_activity', 'Show activity log')}
        </Button>

        {!confirmingDelete ? (
          <Button
            size="sm"
            variant="outline"
            className="text-red-600 border-red-300 hover:bg-red-50"
            onClick={() => setConfirmingDelete(true)}
            data-testid="journey-delete-data"
          >
            <Trash2 className="w-3.5 h-3.5 mr-1" />
            {t('journey.privacy.delete_cta', 'Delete my journey data')}
          </Button>
        ) : (
          <span className="inline-flex items-center gap-2">
            <span className="text-sm text-red-600">
              {t('journey.privacy.delete_confirm', 'Delete everything? This cannot be undone.')}
            </span>
            <Button
              size="sm"
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={deleting}
              onClick={async () => {
                setDeleting(true);
                try {
                  await onDeleteAll();
                } finally {
                  setDeleting(false);
                  setConfirmingDelete(false);
                }
              }}
              data-testid="journey-delete-confirm"
            >
              {deleting
                ? t('journey.privacy.deleting', 'Deleting…')
                : t('journey.privacy.delete_yes', 'Yes, delete')}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setConfirmingDelete(false)}>
              <X className="w-3.5 h-3.5" />
            </Button>
          </span>
        )}
      </div>

      {auditEvents && (
        <ul className="mt-4 border-t border-slate-200 pt-3 space-y-1.5 max-h-64 overflow-y-auto">
          {auditEvents.length === 0 && (
            <li className="text-xs text-slate-500">
              {t('journey.privacy.no_activity', 'No activity recorded yet.')}
            </li>
          )}
          {auditEvents.map((event) => (
            <li key={event.id} className="text-xs text-slate-600 flex items-center gap-2">
              <span className="text-slate-400 tabular-nums flex-shrink-0">
                {new Date(event.created_at).toLocaleString()}
              </span>
              <span>{t(auditActionKey(event.action), event.action)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function NeedHelpDialog({ t, categoryId, taskId, navigate, onClose, onCreated }) {
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // help_request with routing
  const [dialogError, setDialogError] = useState('');

  const submit = async () => {
    setSubmitting(true);
    setDialogError('');
    try {
      const res = await journeyAPI.createHelpRequest({
        category_id: categoryId,
        task_id: taskId || null,
        message,
      });
      setResult(res.data.help_request);
      onCreated();
    } catch (err) {
      console.error('Failed to create help request:', err);
      setDialogError(
        t('journey.help.error', 'Could not send your request. Please try again.')
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-md p-6">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-slate-400 hover:text-slate-600"
          aria-label={t('journey.help.close', 'Close')}
        >
          <X className="w-4 h-4" />
        </button>

        {!result ? (
          <>
            <div className="flex items-center gap-2 mb-2">
              <HelpCircle className="w-5 h-5 text-signal-blue" />
              <h3 className="font-heading text-lg font-bold text-slate-900">
                {t('journey.help.title', 'Ask JC for help')}
              </h3>
            </div>
            <p className="text-sm text-slate-600 mb-1">
              {t(categoryLabelKey(categoryId), categoryId)}
              {taskId && <span> — {t(taskNameKey(taskId), taskId)}</span>}
            </p>
            <p className="text-xs text-slate-500 mb-4">
              {t(
                'journey.help.subtitle',
                'Tell us where you are stuck. Your request is saved to your journey — nothing is sent anywhere else.'
              )}
            </p>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={1000}
              rows={4}
              placeholder={t('journey.help.placeholder', 'What do you need help with? (optional)')}
              className="w-full bg-white border border-slate-300 rounded-md px-3 py-2 text-sm text-slate-800 mb-4"
              data-testid="journey-help-message"
            />
            {dialogError && <p className="text-sm text-red-600 mb-3">{dialogError}</p>}
            <Button
              onClick={submit}
              disabled={submitting}
              className="w-full bg-signal-blue hover:bg-signal-blue-hover"
              data-testid="journey-help-submit"
            >
              {submitting
                ? t('journey.help.sending', 'Saving…')
                : t('journey.help.submit_cta', 'Get help')}
            </Button>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-500" />
              <h3 className="font-heading text-lg font-bold text-slate-900">
                {t('journey.help.saved_title', 'Request saved')}
              </h3>
            </div>
            <p className="text-sm text-slate-600 mb-4">
              {t(
                'journey.help.saved_body',
                'JC has your request. Here is where to get answers right now:'
              )}
            </p>
            <div className="bg-champagne-50 border border-champagne-200 rounded-lg p-3 mb-4">
              <p className="text-xs text-slate-500 mb-1">
                {t('journey.help.suggested_question', 'Suggested question for JC Concierge:')}
              </p>
              <p className="text-sm text-slate-800 italic">
                "{t(helpPromptKey(categoryId), '')}"
              </p>
            </div>
            <Button
              onClick={() => navigate(result.routing?.concierge_path || '/concierge')}
              className="w-full bg-signal-blue hover:bg-signal-blue-hover mb-2"
              data-testid="journey-help-open-concierge"
            >
              {t('journey.help.open_concierge', 'Open JC Concierge')}
            </Button>
            {(result.routing?.links || []).map((link) => (
              <Button
                key={link}
                variant="outline"
                className="w-full mb-2"
                onClick={() => navigate(link)}
              >
                {t(`journey.help.link.${link.replace(/\//g, '')}`, link)}
              </Button>
            ))}
          </>
        )}
      </div>
    </div>
  );
}
